import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FadeUp, SectionHeading } from "./Reveal";

const inputCls =
  "w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm text-jawa-espresso outline-none focus:border-jawa-turquoise";

const CATEGORIES = [
  "Airport arrival & transport",
  "Concierge services",
  "Tours & experiences",
  "Accommodation",
  "Relocation & settling in",
  "Investment resources",
  "Welcome boxes & VIP packages",
  "Other enquiry",
];

export const StartConversation = () => {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", category: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy || done) return;
    setBusy(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        email: form.email,
        journey: "Homepage — Start the conversation",
        message: form.message,
        details: { category: form.category, whatsapp: form.whatsapp },
        website: form.website,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="start-conversation-section" className="bg-jawa-espresso">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <FadeUp>
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-jawa-gold">Talk to us</p>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-jawa-ivory sm:text-5xl">
              Start the conversation
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-jawa-ivory/70">
              Enquiries land directly with the team — expect a personal reply.
            </p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-jawa-ivory/50">
              Tell us what you're planning — an arrival, a move, a tour or a welcome box — and a real person
              from the JÀWÀ team will come back to you.
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            {done ? (
              <div className="border border-jawa-gold/40 bg-jawa-nearblack p-8" data-testid="start-conversation-success">
                <p className="font-serif text-2xl font-medium text-jawa-ivory">Enquiry received.</p>
                <p className="mt-3 text-sm leading-relaxed text-jawa-ivory/65">
                  Thank you — your enquiry has landed with the JÀWÀ team and you'll receive a personal reply by
                  email. Nothing is charged at this stage.
                </p>
                <p className="mt-4 text-xs leading-relaxed text-jawa-ivory/50" data-testid="start-conversation-contact-line">
                  Reach us anytime at <a href="mailto:info@jawaafrica.com" className="font-semibold text-jawa-turquoise">info@jawaafrica.com</a> or
                  WhatsApp <span className="font-semibold text-jawa-ivory/80">+234 915 437 9303</span>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="start-conversation-form">
                <input
                  value={form.website}
                  onChange={set("website")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                />
                <input required value={form.name} onChange={set("name")} placeholder="Full name" data-testid="start-conversation-name" className={inputCls} />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" data-testid="start-conversation-email" className={inputCls} />
                <input value={form.whatsapp} onChange={set("whatsapp")} placeholder="WhatsApp number (optional, with country code)" data-testid="start-conversation-whatsapp" className={inputCls} />
                <select required value={form.category} onChange={set("category")} data-testid="start-conversation-category" className={inputCls}>
                  <option value="" disabled>What can we help you with?</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <textarea required rows={4} value={form.message} onChange={set("message")} placeholder="Your message" data-testid="start-conversation-message" className={inputCls} />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="start-conversation-submit"
                  className="flex w-full items-center justify-center gap-2 bg-jawa-coral px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-ivory hover:text-jawa-espresso disabled:opacity-60"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send enquiry
                </button>
                <p className="text-center text-[11px] leading-relaxed text-jawa-ivory/45">
                  By sending, you agree to our{" "}
                  <Link to="/legal" data-testid="start-conversation-privacy" className="underline hover:text-jawa-turquoise">
                    privacy policy
                  </Link>
                  . We only use your details to reply to your enquiry.
                </p>
              </form>
            )}
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
